import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { formatCurrency, formatDate } from '@/lib/formatters'
import type { PolicyChangeRequest } from '@/models/policyChange'

const money = (amount: string | null) => (amount === null ? '-' : formatCurrency(Number(amount)))

interface QuoteDecision {
  accepting: boolean
  onAccept: () => void
  onDecline: () => Promise<void>
}

interface PolicyChangeQuoteCardProps {
  request: PolicyChangeRequest
  /** Left out once the customer has accepted: the card then shows the price they agreed to. */
  decision?: QuoteDecision
}

/** One price per policy and one decision for all of them: the change goes on every policy or none. */
export function PolicyChangeQuoteCard({ request, decision }: PolicyChangeQuoteCardProps) {
  const policiesById = new Map(request.policies.map((policy) => [policy.id, policy]))
  const expires = request.quotes
    .map((quote) => quote.expiresOn)
    .filter(Boolean)
    .sort()[0]

  return (
    <Card>
      <CardHeader>
        <CardTitle>{decision ? 'Your quote is ready' : 'The quote you accepted'}</CardTitle>
        <p className="text-sm text-neutral-600">
          {decision
            ? 'Accept to make these changes on all of your policies. Decline and nothing changes.'
            : 'These changes go on all of your policies at this price.'}
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col divide-y divide-neutral-100 text-sm">
          <div className="grid grid-cols-[2fr_1fr_1fr] gap-2 pb-2 text-xs text-neutral-500">
            <span>Policy</span>
            <span>Due now</span>
            <span>Yearly change</span>
          </div>
          {request.quotes.map((quote) => {
            const policy = policiesById.get(quote.policyId)
            return (
              <div key={quote.id} className="grid grid-cols-[2fr_1fr_1fr] gap-2 py-2">
                <span>
                  {[policy?.coverages.join(', '), policy?.carrierName].filter(Boolean).join(' · ')}
                  {quote.effectiveDate && (
                    <span className="block text-xs text-neutral-500">
                      From {formatDate(quote.effectiveDate)}
                    </span>
                  )}
                </span>
                <span>{money(quote.proratedChange)}</span>
                <span>{money(quote.annualChange)}</span>
              </div>
            )
          })}
          <div className="grid grid-cols-[2fr_1fr_1fr] gap-2 pt-2 font-semibold">
            <span>Total</span>
            <span>{money(request.proratedChangeTotal)}</span>
            <span>{money(request.annualChangeTotal)}</span>
          </div>
        </div>
        {decision && expires && (
          <p className="text-sm text-neutral-600">This quote is good until {formatDate(expires)}.</p>
        )}
        {decision && (
          <div className="flex justify-end gap-3">
            <ConfirmationDialog
              title="Decline this quote?"
              description="Nothing changes on your policies, and this request closes. You can reopen it later."
              confirmLabel="Decline"
              onConfirm={decision.onDecline}
            >
              <Button type="button" variant="outline">
                Decline
              </Button>
            </ConfirmationDialog>
            <Button
              type="button"
              onClick={decision.onAccept}
              loading={decision.accepting ? 'Accepting…' : undefined}
            >
              Accept quote
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
